import React, { FC, useContext, useEffect, useState, ReactElement } from 'react'
import styled from 'styled-components'
import { Token } from '../../../logic/token/token'
import { TokenSelector } from '../../catalog/TokenSelector'
import { Wei } from '../../../utils/types'
import { PoolWithData, TokenWithData } from '../../../api/api-types'
import { Web3Context } from '../../../hooks/web3'
import { DepositStates, ViewState } from './DepositState'
import { TokenApi } from '../../../api/web3/token-api'
import { EmptyLine } from '../../catalog/EmptyLine'
import { SimpleLoader, SwaapLargeLoader } from '../../catalog/SwaapLoader'
import { DepositPostview } from './DepositPostview'
import {
  DepositResult,
  getOneAssetJoinTokens,
} from '../../../api/web3/deposit-api'
import {
  extractErrorFromState,
  extractErrorMessage,
} from '../../../logic/errors/extract-error-message'
import { FailDepositPanel } from './FailDepositPanel'
import SwapButton from '../../swap/SwapButton'
import { ProxyApi } from '../../../api/web3/proxy-api'
import CurrencyLogo, { Coin } from '../../CurrencyLogo'
import { coinImage } from '../../../assets/coins/coins'
import { SimpleGrid } from '../../catalog/SimpleGrid'
import { useTransactions } from '../../transactions/TransactionContext'
import {
  JoinWith0xArgs,
  JoinWithSwaapArgs,
  OneAssetPreview,
} from './OneAssetPreview'
import { oracleApi } from '../../../api/oracle/oracle-client'
import { fromWei } from '../../../utils/static-web3'
import { swaapRed0 } from '../../Themes'
import { ArrowBackFixed } from '../../catalog/ArrowDownFixed'
import { useNavigate } from 'react-router-dom'
import { createEarnTabEvent } from 'user-events-segment'
import { Amounts } from '../../../logic/token/amounts'
import { createObject } from '../../../utils/objects'
import { ONE_ASSET } from './DepositPopup'
import { DepositAction } from './deposit-reducer'

const OneAssetPanelWrapper = styled.div`
  width: 425px;
`

export interface Props {
  pool: PoolWithData
  onExit: VoidFunction
  onDeposit: (deposit: DepositResult) => void
  onFail: (message?: string) => void
  onHideNavigation: (b: boolean) => void
  slippage: number
  depositDispatcher: React.Dispatch<DepositAction>
}

export const MINIMAL_VALUE_FOR_TOKEN_PRECISION = 0.008

export const OneAssetPanel: FC<Props> = ({
  pool,
  onExit,
  onDeposit,
  onFail,
  onHideNavigation,
  slippage,
  depositDispatcher,
}) => {
  const navigate = useNavigate()

  const [token, setToken] = useState<Token>(pool.tokens[0])
  const [amount, setAmount] = useState<Wei | undefined>(undefined)
  const [nativeAmountNotSafe, setNativeAmountNotSafe] = useState<boolean>(false)

  const tokens: Token[] = getOneAssetJoinTokens(pool)
  const { web3, account, appChainID, proxy } = useContext(Web3Context)

  const [balances, setBalances] = useState(new Map<Token, Wei | undefined>())

  // TODO: something tricky: why setAllowing not used ?
  const [allowing, setAllowing] = useState(false)
  const [tradeWithSwaapPool, setTradeWithSwaapPool] = useState(false)
  const [depositArgs, setDepositArgs] = useState<JoinWithSwaapArgs>()
  const [previewLoading, setPreviewLoading] = useState(false)
  const [tooSmall, setTooSmall] = useState(false)

  const {
    CONNECT,
    CHOOSE_AMOUNT,
    VALID,
    PREVIEW,
    ALLOWANCE_LOADING,
    ALLOWANCE_DONE,
    PROCESSING,
    TRANSACTION_SUCCESS,
    ALLOWANCE_FAILED,
    TRANSACTION_FAILED,
  } = DepositStates
  const [state, setState] = useState<ViewState>(CHOOSE_AMOUNT)

  const [alreadyAllowed, setAlreadyAllowed] = useState(false)
  const [buttonMessage, setButtonMessage] = useState<string | ReactElement>(
    getMessage(state)
  )
  const [buttonEnabled, setButtonEnabled] = useState<boolean>(false)
  // We don't want to change the enabling or message of the button while values are loading: flickering problem

  useEffect(() => {
    reset()
  }, [])

  useEffect(() => {
    if (state === PREVIEW) {
      onHideNavigation(true)
    }
    if (state.isIn([PREVIEW, ALLOWANCE_DONE])) {
      // After Allowance done, we go to a substate with previewLoading
      setPreviewLoading(true)
      depositDispatcher({
        type: 'show-preview',
        data: {
          bringIn: new Amounts().add(token, amount!),
        },
      })
    }
    setButtonMessage(getMessage(state))
    setButtonEnabled(enableButton())
  }, [state])

  useEffect(() => {
    state.subState = {}
    if (checkValid()) {
      // setValid may have not been yet called at this point
      setState(VALID)
      if (state.isIn([CHOOSE_AMOUNT, VALID])) {
        // TODO: it makes too much call probably. COuld be done only on preview
        loadAllowedTokens().catch(e => console.error(e))
      }
    } else {
      setState(CHOOSE_AMOUNT.clear())

      if (amount) {
        const joinValue =
          oracleApi.getPrice(token.symbol) * fromWei(amount, token.decimals)
        if (joinValue > 0 && joinValue < MINIMAL_VALUE_FOR_TOKEN_PRECISION) {
          setTooSmall(true)
        }
      }
      setButtonMessage(getMessage(state))
    }
  }, [amount])

  useEffect(() => {
    setButtonMessage(getMessage(state))
  }, [previewLoading])
  useEffect(() => {
    setButtonMessage(getMessage(state))
  }, [tooSmall])

  function loadBalances(token: Token) {
    if (!web3 || !account) {
      // parent will ask to connect
      console.warn('not connected')
      return
    }
    const tokenApi = new TokenApi(web3, account, token)

    return tokenApi
      .getERC20Balance()
      .then(balance => {
        balances.set(token, balance)
      })
      .catch(console.error)
  }

  function checkValid(): boolean {
    if (!web3 || !account) {
      return false
    }
    if (amount === undefined) {
      return false
    }
    if (state.subState && state.subState.error) {
      return false
    }

    const joinValue =
      oracleApi.getPrice(token.symbol) * fromWei(amount, token.decimals)

    const isValid =
      Number(amount) > 0 &&
      joinValue >= MINIMAL_VALUE_FOR_TOKEN_PRECISION &&
      enoughBalance(amount, token)

    return isValid
  }

  // amount must be defined
  function enoughBalance(_amount: Wei, token: Token): boolean {
    return Number(_amount!) <= Number(balances.get(token) || 0)
  }

  async function loadAllowedTokens() {
    const proxyApi = new ProxyApi(web3!, account!, appChainID, proxy)
    try {
      const initialAllowed = await proxyApi.isAllowed(token, amount!)
      if (initialAllowed) {
        setAlreadyAllowed(true)
      } else {
        setAlreadyAllowed(false)
      }
    } catch (error: any) {
      console.error(error)
      setAlreadyAllowed(false)
    }
  }

  function getMessage(state: ViewState): string | ReactElement {
    switch (state.key) {
      case CONNECT.key:
        return 'Connect'
      case CHOOSE_AMOUNT.key: {
        if (state.subState && state.subState.error) {
          return state.subState.message || 'Transaction cancelled'
        }
        if (Number(amount) === 0) {
          return 'Choose amount'
        }
        if (Number(amount) > Number(balances.get(token))) {
          return 'Not enough balance'
        }
        if (tooSmall) {
          return 'Amount too small'
        }
        return 'Choose amount'
      }
      case VALID.key:
        return 'Preview'
      case PREVIEW.key:
        if (alreadyAllowed) {
          return previewLoading ? 'Fetching the best route...' : 'Deposit'
        } else {
          return previewLoading
            ? 'Fetching the best route...'
            : 'Allowance is needed'
        }

      case ALLOWANCE_LOADING.key:
        return (
          <div
            style={{
              display: 'flex',
              alignSelf: 'center',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <span className="pr10">Approving</span>
            <Coin symbol={token.symbol} />
          </div>
        )
      case ALLOWANCE_DONE.key:
        return previewLoading ? 'Fetching the best route...' : 'Deposit'
      case PROCESSING.key:
        return 'Processing deposit'
      case TRANSACTION_SUCCESS.key:
      case TRANSACTION_FAILED.key:
      case ALLOWANCE_FAILED.key:
        return 'OK'
    }
    return 'Deposit'
  }

  async function allow() {
    const proxyApi = new ProxyApi(web3!, account!, appChainID, proxy)
    setState(ALLOWANCE_LOADING)
    try {
      await proxyApi.allowOneAsset(pool, amount!, token)
      setState(ALLOWANCE_DONE)
    } catch (error) {
      console.error(error)
      setState(ALLOWANCE_FAILED.withError(extractErrorMessage(error)))
    }
  }

  const { dispatcher } = useTransactions()

  async function executeOneAssetDeposit(): Promise<DepositResult> {
    const proxyApi = new ProxyApi(web3!, account!, appChainID, proxy)
    if (tradeWithSwaapPool) {
      const args = depositArgs!
      depositDispatcher({
        type: 'execute-deposit',
        mode: '1_asset_with_swaap',
        data: {
          bringIn: new Amounts().add(token, args.amount),
          amounts: Amounts.fromMap(args.amountsIn),
          expected_minted_shares: args.expectedShare,
          minimal_minted_shares: args.minimumShare,
        },
      })
      try {
        return await proxyApi.depositWithOneAsset(
          pool,
          args.amount,
          args.minimumShare,
          token,
          dispatcher
        )
      } catch (error: any) {
        dispatchError(depositDispatcher, error, args, amount!)
        throw error
      }
    } else {
      // Using 0x
      const args = depositArgs as JoinWith0xArgs
      depositDispatcher({
        type: 'execute-deposit',
        mode: '1_asset_with_aggregator',
        data: {
          bringIn: new Amounts().add(token, args.amount),
          amounts: Amounts.fromMap(args.amountsIn),
          expected_minted_shares: args.expectedShare,
          minimal_minted_shares: args.minimumShare,
        },
      })
      try {
        return await proxyApi.depositOneAssetViaZeroEx(
          pool,
          args.fillQuotes,
          token,
          amount!,
          args.expectedShare,
          args.amountsIn,
          dispatcher
        )
      } catch (error: any) {
        dispatchError(depositDispatcher, error, args, amount!)
        throw error
      }
    }
  }

  function reset() {
    Promise.all(tokens.map(loadBalances)).then(() =>
      setBalances(new Map(balances))
    )

    setToken(tokens[0])
    setAmount(undefined) // should rebalance
  }

  async function handleClick() {
    if ((state === PREVIEW && alreadyAllowed) || state === ALLOWANCE_DONE) {
      try {
        setState(PROCESSING)
        const deposit = await executeOneAssetDeposit()
        onDeposit(deposit)
        setState(TRANSACTION_SUCCESS.withSubState({ deposit }))
      } catch (error: any) {
        console.error('putting state', error)
        // TODO: see useReducer without action for this
        setState(TRANSACTION_FAILED.withError(extractErrorMessage(error)))
      }
    } else if (state === TRANSACTION_SUCCESS) {
      onExit()
      navigate('/')
    } else if (state.isIn([TRANSACTION_FAILED, ALLOWANCE_FAILED])) {
      onFail(extractErrorFromState(state))
    } else {
      setState(state.next())
    }
  }

  function enableButton(): boolean {
    if (state.isBefore(VALID)) {
      return checkValid()
    } else if (state === PREVIEW) {
      return alreadyAllowed
    } else {
      return state.isIn([
        PREVIEW,
        ALLOWANCE_DONE,
        TRANSACTION_SUCCESS,
        ALLOWANCE_FAILED,
        TRANSACTION_FAILED,
      ])
    }
  }

  // This is a trick so that we don't have to store the form state between back
  // TODO: It could be better to store the state
  function displayTokenSelectorBlock(): string {
    return state.isIn([PREVIEW, ALLOWANCE_DONE]) ? 'none' : 'block'
  }
  return (
    <OneAssetPanelWrapper>
      <EmptyLine />

      <div>
        {state.isIn([
          CONNECT,
          CHOOSE_AMOUNT,
          VALID,
          PREVIEW,
          ALLOWANCE_DONE,
        ]) && (
          <>
            <div style={{ display: displayTokenSelectorBlock() }}>
              <p className="light fs12">Join the pool with only 1 token:</p>
              <TokenSelector
                tokens={tokens}
                onTokenChange={newToken => {
                  reset()
                  setToken(newToken as TokenWithData)
                  createEarnTabEvent(pool).selected_token(
                    ONE_ASSET,
                    token.symbol,
                    newToken.symbol
                  )
                }}
                onAmountChange={(amount: Wei | undefined) => {
                  // events
                  if (amount !== undefined) {
                    if (amount === balances.get(token)) {
                      // we clicked max
                      createEarnTabEvent(pool).clicked_max_token(
                        ONE_ASSET,
                        token.symbol,
                        fromWei(amount || '0', token.decimals)
                      )
                    } else {
                      createEarnTabEvent(pool).entered_amount_token(
                        ONE_ASSET,
                        token.symbol,
                        fromWei(amount || '0', token.decimals)
                      )
                    }
                  }

                  // logic
                  const b = BigInt(balances.get(token) || '0')

                  // We are testing if we add enough Matic for deposit+transaction cost
                  const nativeAmountNotSafe =
                    token != undefined &&
                    token.address ==
                      '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' &&
                    amount != undefined &&
                    enoughBalance(amount, token) &&
                    b <= BigInt(amount) + BigInt('200000000000000000')

                  const safeNativeAmount = nativeAmountNotSafe
                    ? (b > BigInt('200000000000000000')
                        ? (b - BigInt('200000000000000000')).toString()
                        : 0
                      ).toString()
                    : amount

                  setNativeAmountNotSafe(nativeAmountNotSafe)
                  setAmount(safeNativeAmount)

                  return safeNativeAmount
                }}
                amount={amount}
              />
              {nativeAmountNotSafe && (
                <p
                  className="light fs12"
                  style={{ textAlign: 'left', color: swaapRed0 }}
                >
                  To ensure a smooth transaction, at least 0.1 MATIC must be
                  left in your wallet to pay for gas fees.
                </p>
              )}
              <p className="light fs12" style={{ textAlign: 'left' }}>
                You can select a token either from the pool's tokens or among a
                list of blue chips assets.
              </p>
              <p className="light fs12" style={{ textAlign: 'left' }}>
                To match the pool's composition, we will automatically swap your
                token at the best rate using{' '}
                <span className={'numeric'}>0x Protocol</span>.
              </p>
            </div>
          </>
        )}
        {state.isIn([PREVIEW, ALLOWANCE_DONE]) && (
          <div className={'vertical'}>
            <Back
              onClick={() => {
                onHideNavigation(false)
                setState(VALID)
              }}
            >
              <ArrowBackFixed /> <span className={'ml10'}>Back</span>
            </Back>

            <OneAssetPreview
              token={token}
              amount={amount!}
              pool={pool}
              shareSlippage={slippage}
              onDepositError={message => {
                setPreviewLoading(false)
                if (message !== undefined) {
                  console.error('on deposit error', message)
                  setState(CHOOSE_AMOUNT.withError(message))
                  setButtonMessage(message)
                  setButtonEnabled(false)
                  onHideNavigation(false)
                  setPreviewLoading(false)
                }
              }}
              onDepositReady={(tradeWithSwaap, args) => {
                setTradeWithSwaapPool(tradeWithSwaap)
                setPreviewLoading(false)
                setDepositArgs(args)
              }}
            />
          </div>
        )}
        {state === ALLOWANCE_LOADING && (
          <SimpleGrid>
            <SwaapLargeLoader />
          </SimpleGrid>
        )}
        {state === PROCESSING && (
          <>
            <SwaapLargeLoader />
          </>
        )}
        {state === TRANSACTION_SUCCESS && (
          <>
            {/* TODO : We should probably no more reuse this DepositPostView as there is only one token */}
            <DepositPostview
              deposit={state.subState!.deposit!}
              amounts={new Map<Token, Wei>().set(token, depositArgs!.amount)}
              residues={
                tradeWithSwaapPool
                  ? undefined
                  : pool.tokens.filter(t => t.symbol !== token.symbol)
              }
            />
          </>
        )}
        {state.isIn([TRANSACTION_FAILED, ALLOWANCE_FAILED]) && (
          <FailDepositPanel state={state} />
        )}
        <EmptyLine />
        <SimpleGrid>
          {!alreadyAllowed && state === PREVIEW && !previewLoading && (
            <SwapButton
              handleOnClick={() => allow()}
              message={`Allow Swaap to use your ${token.symbol}`}
              isReadyToSwap={!allowing}
              waiting={allowing}
              icon={<CurrencyLogo src={coinImage(token.symbol)!} />}
              isSmall={true}
            />
          )}
          <SwapButton
            handleOnClick={() => handleClick()}
            message={buttonMessage}
            isReadyToSwap={buttonEnabled && !previewLoading}
          />
        </SimpleGrid>
      </div>
    </OneAssetPanelWrapper>
  )
}

export const Back = styled.div`
  cursor: pointer;
  display: flex;
  margin-bottom: 20px;
  //padding: 10px;
`

function dispatchError(
  depositDispatcher: any,
  error: any,
  args: any,
  amount: Wei
) {
  console.error(error)
  let hash = error.hash
  const step = error.hash ? 'revert' : 'not_accepted'
  const reason = extractErrorMessage(error)
  depositDispatcher({
    type: 'failure',
    data: {
      step,
      reason,
      amounts: Amounts.fromMap(args.amountsIn),
      bringIn: new Amounts().add(args.token, amount!),
      expected_minted: args.expectedShare,
      hash,
    },
  })
}
